/**
 * TourAudioConsentBanner — One-time prompt that asks the user to enable tour
 * audio so the browser's autoplay policy allows subsequent narrations to play
 * without a tap.
 *
 * Why this exists: Chrome (and most modern browsers) block autoplay until the
 * user has explicitly interacted with the page. That means the very first
 * Quick Tip on the user's first visit always needs a manual tap. By presenting
 * a single "Enable audio for tours" button up-front, the click registers the
 * required gesture and ALL subsequent tour audio plays automatically.
 *
 * Storage: localStorage key 'tour_audio_consent_v1' = 'enabled' | 'declined'.
 * Once set (either value) the banner is never shown again on this device.
 */
import { useEffect, useState } from 'react';
import { Volume2, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const STORAGE_KEY = 'tour_audio_consent_v1';

/** Returns true if the user has previously enabled tour audio on this device. */
export const hasTourAudioConsent = () => {
  try { return localStorage.getItem(STORAGE_KEY) === 'enabled'; } catch { return false; }
};

/** Plays a 1-frame silent MP3 to "unlock" the browser's autoplay policy.
 *  Must be called inside the same call stack as the user gesture (a click handler). */
const unlockAudioOnGesture = async () => {
  try {
    // 1-frame silent MP3 (base64) — about 100 bytes, valid header.
    const silent = 'SUQzAwAAAAAAEFRYWFgAAAAGAAADAGNhAP/7kGQAAAAAAA';
    const a = new Audio(`data:audio/mpeg;base64,${silent}`);
    a.volume = 0;
    await a.play().catch(() => {});
    a.pause();
  } catch { /* best-effort */ }
};

export const TourAudioConsentBanner = () => {
  const { user } = useAuth();
  const [decided, setDecided] = useState(true);  // assume decided until we know otherwise

  useEffect(() => {
    if (!user) return;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      setDecided(stored === 'enabled' || stored === 'declined');
    } catch { setDecided(true); }
  }, [user]);

  if (!user || decided) return null;

  const handleEnable = async () => {
    await unlockAudioOnGesture();
    try { localStorage.setItem(STORAGE_KEY, 'enabled'); } catch { /* private mode */ }
    setDecided(true);
    // Fire a window event so MiniTour can immediately retry an in-flight play.
    window.dispatchEvent(new CustomEvent('tour-audio-consent', { detail: { consent: 'enabled' } }));
  };

  const handleDecline = () => {
    try { localStorage.setItem(STORAGE_KEY, 'declined'); } catch { /* ignore */ }
    setDecided(true);
    window.dispatchEvent(new CustomEvent('tour-audio-consent', { detail: { consent: 'declined' } }));
  };

  return (
    <div
      className="relative mx-3 md:mx-6 mt-3 bg-gradient-to-r from-teal-50 via-cyan-50 to-blue-50 border border-teal-200 rounded-xl px-3 py-2.5 flex items-center gap-3 shadow-sm z-[9999]"
      data-testid="tour-audio-consent-banner"
    >
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-teal-500 text-white flex items-center justify-center">
        <Volume2 className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[12px] font-semibold text-teal-900 leading-tight">
          Enable narrated walkthroughs?
        </p>
        <p className="text-[11px] text-teal-700/80 mt-0.5 leading-tight">
          One click and every Quick Tip will read itself out loud — no need to tap each one.
        </p>
      </div>
      <button
        onClick={handleEnable}
        className="flex-shrink-0 px-3 py-1.5 text-[11px] font-semibold bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
        data-testid="tour-audio-consent-enable"
      >
        Enable audio
      </button>
      <button
        onClick={handleDecline}
        className="flex-shrink-0 text-teal-600/70 hover:text-teal-800 transition-colors p-1"
        title="Not now"
        data-testid="tour-audio-consent-decline"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export default TourAudioConsentBanner;
